import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, MailCheck } from 'lucide-react'
import { api, ApiError } from '@/lib/api'
import { AuthLayout } from '@/components/AuthLayout'
import { Button, Field, Input } from '@/components/ui'
import { KaproLogo } from '@/components/Logo'
import { useT } from '@/i18n/I18nProvider'

/**
 * "I forgot my password."
 *
 * Two things this screen has to get right, and they pull against each other.
 *
 * The first is that the answer is always the same. The server will not say
 * whether the account exists — the employee code is printed on badges and
 * stickers all over the plant, so an endpoint that distinguished a real code
 * from an invented one would enumerate the payroll for anyone walking past a
 * machine. The success panel therefore says "if the account exists", which is
 * a slightly awkward sentence that has to stay awkward.
 *
 * The second is that for most people here this route does not work at all, and
 * pretending otherwise is worse than useless. A factory worker typically has no
 * e-mail address; recovery by mail needs a **verified** one. So the other route
 * — a supervisor issues an activation code — is stated up front, before the
 * form, not hidden behind a failure. Nobody should type their code, wait, check
 * a mailbox they do not have, and only then find out.
 */
export function ForgotPasswordPage() {
  const t = useT()
  const [identifier, setIdentifier] = useState('')
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    setSubmitting(true)

    try {
      await api.post('/auth/forgot-password', { identifier })
      setSent(true)
    } catch (err) {
      // Only ever a throttle or a network failure — the endpoint answers 200
      // for every outcome that depends on whether the account exists.
      setError(
        err instanceof ApiError
          ? (err.fieldError('identifier') ?? err.message)
          : t('profile.forgot.sendFailed'),
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AuthLayout>
      <div className="flex flex-col items-center text-center">
        <KaproLogo size="lg" />
      </div>

      {sent ? (
        <div className="mt-8">
          <div className="flex flex-col items-center text-center">
            <span className="flex size-12 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300">
              <MailCheck className="size-6" aria-hidden />
            </span>

            <h1 className="mt-4 text-xl font-semibold">{t('profile.forgot.sent.title')}</h1>

            <p className="muted mt-2 text-sm">{t('profile.forgot.sent.body')}</p>

            <p className="muted mt-3 text-sm">{t('profile.forgot.sent.expiry')}</p>
          </div>

          <p className="mt-6 rounded-xl bg-amber-50 px-3.5 py-3 text-sm text-amber-900 dark:bg-amber-950/60 dark:text-amber-200">
            {t('profile.forgot.sent.noMail')}
          </p>

          <Button
            variant="secondary"
            fullWidth
            className="mt-6"
            render={<Link to="/prijava" />}
            nativeButton={false}
          >
            {t('profile.backToLogin')}
          </Button>
        </div>
      ) : (
        <>
          <div className="mt-8 text-center">
            <h1 className="text-xl font-semibold">{t('profile.forgot.title')}</h1>
            <p className="muted mt-2 text-sm">{t('profile.forgot.intro')}</p>
          </div>

          <form
            onSubmit={handleSubmit}
            className="mt-6 space-y-4 [@media(max-height:700px)]:mt-4"
            noValidate
          >
            <Field
              label={t('profile.forgot.identifier')}
              required
              hint={t('profile.forgot.identifier.hint')}
            >
              <Input
                name="identifier"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder={t('profile.forgot.identifier.placeholder')}
                autoComplete="username"
                // Same reasoning as the login field: a phone keyboard will
                // happily turn "emp-1042" into "Emp-1042".
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                enterKeyHint="go"
                required
                autoFocus
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? 'forgot-error' : undefined}
              />
            </Field>

            {error && (
              <p
                id="forgot-error"
                role="alert"
                className="rounded-xl bg-rose-50 px-3 py-2.5 text-sm text-rose-700 dark:bg-rose-950 dark:text-rose-300"
              >
                {error}
              </p>
            )}

            <Button type="submit" fullWidth loading={submitting} disabled={!identifier.trim()}>
              {t('profile.forgot.submit')}
            </Button>
          </form>

          {/* Stated before anyone waits on a mailbox they may not have. */}
          <p className="muted mt-6 rounded-xl border border-[var(--border-subtle)] px-3.5 py-3 text-sm [@media(max-height:700px)]:mt-4">
            <strong className="font-medium">{t('profile.forgot.noEmail.title')}</strong>{' '}
            {t('profile.forgot.noEmail.body')}
          </p>

          <div className="mt-6 text-center [@media(max-height:700px)]:mt-3">
            <Link
              to="/prijava"
              className="muted inline-flex min-h-11 items-center gap-1.5 text-sm underline-offset-4 hover:underline"
            >
              <ArrowLeft className="size-4" aria-hidden />
              {t('profile.backToLogin')}
            </Link>
          </div>
        </>
      )}
    </AuthLayout>
  )
}
